import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShieldAlert, Ban, Trash2, LogOut, UserPlus, Eraser, RefreshCw, Power, Smartphone, Check, X, KeyRound, Copy } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { useAuth } from '../../../contexts/AuthContext';
import {
  listUsers, createUser, deleteUser, forceLogoutUser, logoutAllOwn,
  listBlockedIps, blockIp, unblockIp, purge, resetUserPassword,
  listDevices, approveDevice, rejectDevice, deleteDevice,
} from '../../../services/adminConsole';

const roleLabel = (t) => ({ fondateur: 'Fondateur', invite: 'Invité', membre: 'Membre' }[t] || t);

const readUA = (ua) => {
  if (!ua) return 'Appareil inconnu';
  const os = /Windows/.test(ua) ? 'Windows' : /iPhone|iPad|iOS/.test(ua) ? 'iOS' : /Android/.test(ua) ? 'Android' : /Mac OS X|Macintosh/.test(ua) ? 'macOS' : /Linux/.test(ua) ? 'Linux' : 'OS inconnu';
  const br = /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : 'Navigateur';
  return `${br} sur ${os}`;
};

const deviceStatusBadge = (s) => ({
  approved: 'bg-emerald-500/15 text-emerald-600',
  pending: 'bg-amber-500/15 text-amber-600',
  rejected: 'bg-red-500/15 text-red-600',
}[s] || 'bg-muted text-muted-foreground');

const deviceStatusLabel = (s) => ({ approved: 'Approuvé', pending: 'En attente', rejected: 'Refusé' }[s] || s);

const AdminConsole = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [blocked, setBlocked] = useState([]);
  const [devices, setDevices] = useState([]);
  const [resetInfo, setResetInfo] = useState(null);
  const [pendingDevices, setPendingDevices] = useState(0);
  const [newIp, setNewIp] = useState('');
  const [ipReason, setIpReason] = useState('');
  const [inactiveDays, setInactiveDays] = useState(180);

  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', password: '', phone: '', role: 'membre', age: '', address: '' });

  const flash = (m, isErr = false) => {
    if (isErr) { setErr(m); setMsg(''); } else { setMsg(m); setErr(''); }
    setTimeout(() => { setMsg(''); setErr(''); }, 4000);
  };

  const loadAll = async () => {
    try {
      const [u, b, d] = await Promise.all([listUsers(search), listBlockedIps(), listDevices()]);
      setUsers(u.users || []);
      setBlocked(b.blocked_ips || []);
      setDevices(d.devices || []);
      setPendingDevices(d.pending || 0);
    } catch (e) { flash(e.response?.data?.detail || e.message, true); }
  };

  useEffect(() => { if (user?.user_type === 'fondateur') loadAll(); /* eslint-disable-next-line */ }, [user]);

  const handleCreate = async () => {
    try {
      await createUser({
        ...form,
        age: form.age ? Number(form.age) : null,
      });
      flash('✅ Compte créé');
      setForm({ first_name: '', last_name: '', email: '', password: '', phone: '', role: 'membre', age: '', address: '' });
      loadAll();
    } catch (e) { flash(e.response?.data?.detail || e.message, true); }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Supprimer définitivement le compte ${u.email} ?`)) return;
    try { await deleteUser(u.id); flash(`🗑️ ${u.email} supprimé`); loadAll(); }
    catch (e) { flash(e.response?.data?.detail || e.message, true); }
  };

  const handleForceLogout = async (u) => {
    if (!window.confirm(`Déconnecter ${u.email} de tous ses appareils ?`)) return;
    try { await forceLogoutUser(u.id); flash(`🔌 ${u.email} déconnecté partout`); }
    catch (e) { flash(e.response?.data?.detail || e.message, true); }
  };

  const handleResetPassword = async (u) => {
    const custom = window.prompt(`Nouveau mot de passe pour ${u.email} ?\n\nLaissez VIDE pour en générer un automatiquement (recommandé). Min. 8 caractères.`, '');
    if (custom === null) return;
    try {
      const res = await resetUserPassword(u.id, custom.trim() || undefined);
      setResetInfo({ email: res.email, password: res.new_password });
      flash(`🔑 Mot de passe réinitialisé pour ${res.email}`);
      loadAll();
    } catch (e) { flash(e.response?.data?.detail || e.message, true); }
  };

  const handleLogoutAll = async () => {
    if (!window.confirm('Vous déconnecter de TOUS vos appareils ? Vous devrez vous reconnecter.')) return;
    try {
      await logoutAllOwn();
      await logout();
      navigate('/admin/login');
    } catch (e) { flash(e.response?.data?.detail || e.message, true); }
  };

  const handleBlockIp = async () => {
    if (!newIp.trim()) return;
    try { await blockIp(newIp.trim(), ipReason); flash(`🚫 IP ${newIp} bloquée`); setNewIp(''); setIpReason(''); loadAll(); }
    catch (e) { flash(e.response?.data?.detail || e.message, true); }
  };

  const handleUnblockIp = async (ip) => {
    try { await unblockIp(ip); flash(`✅ IP ${ip} débloquée`); loadAll(); }
    catch (e) { flash(e.response?.data?.detail || e.message, true); }
  };

  const handleApproveDevice = async (d, days) => {
    try { await approveDevice(d.id, days); flash(`✅ Appareil de ${d.email} approuvé${days ? ` (30 jours)` : ''}`); loadAll(); }
    catch (e) { flash(e.response?.data?.detail || e.message, true); }
  };
  const handleRejectDevice = async (d) => {
    try { await rejectDevice(d.id); flash(`🚫 Appareil de ${d.email} refusé`); loadAll(); }
    catch (e) { flash(e.response?.data?.detail || e.message, true); }
  };
  const handleDeleteDevice = async (d) => {
    if (!window.confirm(`Supprimer cet appareil de ${d.email} ? Il devra être ré-approuvé à la prochaine connexion.`)) return;
    try { await deleteDevice(d.id); flash(`🗑️ Appareil supprimé`); loadAll(); }
    catch (e) { flash(e.response?.data?.detail || e.message, true); }
  };

  const handlePurge = async (target) => {
    const labels = { audit: "l'historique des connexions/audit", sessions: 'toutes les sessions actives', inactive: `les comptes inactifs depuis ${inactiveDays} jours` };
    if (!window.confirm(`Confirmer la purge de ${labels[target]} ? Action irréversible.`)) return;
    try { const r = await purge(target, inactiveDays); flash(`🧹 Purge effectuée (${r.purged} élément(s))`); loadAll(); }
    catch (e) { flash(e.response?.data?.detail || e.message, true); }
  };

  if (!user || user.user_type !== 'fondateur') return <div className="p-6 text-red-500">Accès réservé au fondateur</div>;

  return (
    <div className="min-h-screen bg-background" data-testid="admin-console-page">
      <header className="border-b border-border bg-card/50 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" asChild><Link to="/dashboard/fondateur"><ArrowLeft className="h-4 w-4 mr-1" /> Retour</Link></Button>
            <div className="flex items-center gap-2">
              <ShieldAlert className="h-6 w-6 text-red-500" />
              <h1 className="text-lg font-semibold">Centre de sécurité & comptes</h1>
            </div>
          </div>
          <Button variant="destructive" size="sm" onClick={handleLogoutAll} data-testid="btn-logout-all">
            <Power className="h-4 w-4 mr-2" /> Me déconnecter de tous mes appareils
          </Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {msg && <div className="rounded-md bg-emerald-500/10 text-emerald-600 px-4 py-2 text-sm" data-testid="console-msg">{msg}</div>}
        {err && <div className="rounded-md bg-red-500/10 text-red-600 px-4 py-2 text-sm" data-testid="console-err">{err}</div>}

        {/* Appareils & nouvelles connexions */}
        <Card className="border-emerald-500/20" data-testid="devices-card">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Smartphone className="h-5 w-5 text-emerald-500" />
              <CardTitle className="text-base">Appareils & approbation des connexions</CardTitle>
              {pendingDevices > 0 && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-600" data-testid="pending-devices-count">{pendingDevices} en attente</span>
              )}
            </div>
            <CardDescription>Chaque compte est lié à son premier appareil. Tout nouvel appareil est bloqué et doit être approuvé ici.</CardDescription>
          </CardHeader>
          <CardContent className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-muted text-left">
                  <th className="p-2">Compte</th><th className="p-2">Appareil</th><th className="p-2">IP</th>
                  <th className="p-2">Statut</th><th className="p-2">Vu le</th><th className="p-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {devices.map((d) => (
                  <tr key={d.id} className="border-t" data-testid="device-row">
                    <td className="p-2">{d.email}{d.first_device && <span className="ml-1 text-xs text-muted-foreground">(1er)</span>}</td>
                    <td className="p-2" title={d.user_agent || ''}>{readUA(d.user_agent)}</td>
                    <td className="p-2 font-mono text-xs">{d.ip || '—'}</td>
                    <td className="p-2"><span className={`text-xs px-2 py-0.5 rounded-full ${deviceStatusBadge(d.status)}`}>{deviceStatusLabel(d.status)}</span>{d.status === 'approved' && d.trust_days && <span className="ml-1 text-xs text-muted-foreground">({d.trust_days}j)</span>}</td>
                    <td className="p-2">{d.last_seen_at ? new Date(d.last_seen_at).toLocaleString('fr-FR') : '—'}</td>
                    <td className="p-2">
                      <div className="flex gap-1 flex-wrap">
                        {d.status !== 'approved' && (
                          <>
                            <Button size="sm" variant="outline" className="text-emerald-600 hover:bg-emerald-500/10" onClick={() => handleApproveDevice(d)} title="Approuver définitivement" data-testid={`btn-approve-device-${d.id}`}><Check className="h-3.5 w-3.5" /></Button>
                            <Button size="sm" variant="outline" className="text-emerald-600 hover:bg-emerald-500/10 text-xs px-2" onClick={() => handleApproveDevice(d, 30)} title="Approuver 30 jours" data-testid={`btn-approve-device-30d-${d.id}`}>30j</Button>
                          </>
                        )}
                        {d.status !== 'rejected' && (
                          <Button size="sm" variant="outline" className="text-amber-600 hover:bg-amber-500/10" onClick={() => handleRejectDevice(d)} title="Refuser" data-testid={`btn-reject-device-${d.id}`}><X className="h-3.5 w-3.5" /></Button>
                        )}
                        <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-500/10" onClick={() => handleDeleteDevice(d)} title="Supprimer" data-testid={`btn-delete-device-${d.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {devices.length === 0 && <tr><td className="p-3 text-muted-foreground" colSpan="6">Aucun appareil enregistré.</td></tr>}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Blocage IP */}
        <Card className="border-red-500/20">
          <CardHeader>
            <div className="flex items-center gap-2"><Ban className="h-5 w-5 text-red-500" /><CardTitle className="text-base">Adresses IP bloquées</CardTitle></div>
            <CardDescription>Une IP bloquée n'a plus accès à aucune fonctionnalité du site (sauf vous, le fondateur).</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-2">
              <Input placeholder="Adresse IP (ex: 203.0.113.5)" value={newIp} onChange={(e) => setNewIp(e.target.value)} data-testid="input-block-ip" />
              <Input placeholder="Raison (optionnel)" value={ipReason} onChange={(e) => setIpReason(e.target.value)} data-testid="input-block-reason" />
              <Button onClick={handleBlockIp} className="bg-red-500 hover:bg-red-600 shrink-0" data-testid="btn-block-ip"><Ban className="h-4 w-4 mr-2" />Bloquer</Button>
            </div>
            <div className="space-y-2">
              {blocked.length === 0 && <p className="text-sm text-muted-foreground">Aucune IP bloquée.</p>}
              {blocked.map((b) => (
                <div key={b.ip} className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm" data-testid="blocked-ip-row">
                  <div><span className="font-mono font-semibold">{b.ip}</span>{b.reason && <span className="text-muted-foreground ml-2">— {b.reason}</span>}</div>
                  <Button size="sm" variant="outline" onClick={() => handleUnblockIp(b.ip)} data-testid={`btn-unblock-${b.ip}`}>Débloquer</Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Purge */}
        <Card className="border-amber-500/20">
          <CardHeader>
            <div className="flex items-center gap-2"><Eraser className="h-5 w-5 text-amber-500" /><CardTitle className="text-base">Purge des données</CardTitle></div>
            <CardDescription>Nettoyez les journaux, les sessions ou les comptes inactifs. Actions irréversibles.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap items-end gap-3">
            <Button variant="outline" onClick={() => handlePurge('audit')} data-testid="btn-purge-audit"><Eraser className="h-4 w-4 mr-2" />Vider l'historique de connexion</Button>
            <Button variant="outline" onClick={() => handlePurge('sessions')} data-testid="btn-purge-sessions"><Eraser className="h-4 w-4 mr-2" />Terminer toutes les sessions</Button>
            <div className="flex items-end gap-2">
              <div>
                <label className="block text-xs mb-1 text-muted-foreground">Inactif depuis (jours)</label>
                <Input type="number" className="w-32" value={inactiveDays} onChange={(e) => setInactiveDays(Number(e.target.value))} data-testid="input-inactive-days" />
              </div>
              <Button variant="outline" onClick={() => handlePurge('inactive')} data-testid="btn-purge-inactive"><Trash2 className="h-4 w-4 mr-2" />Supprimer comptes inactifs</Button>
            </div>
          </CardContent>
        </Card>

        {/* Créer un compte */}
        <Card className="border-primary/20">
          <CardHeader>
            <div className="flex items-center gap-2"><UserPlus className="h-5 w-5 text-primary" /><CardTitle className="text-base">Ajouter un compte utilisateur</CardTitle></div>
          </CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <Input placeholder="Prénom" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="input-first-name" />
            <Input placeholder="Nom" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="input-last-name" />
            <Input placeholder="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="input-email" />
            <Input placeholder="Mot de passe (min. 8)" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="input-password" />
            <Input placeholder="Téléphone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="input-phone" />
            <select className="rounded-md border border-border bg-background px-3 py-2 text-sm" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="select-role">
              <option value="membre">Membre</option>
              <option value="invite">Invité</option>
            </select>
            <Input placeholder="Âge" type="number" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} data-testid="input-age" />
            <Input placeholder="Adresse" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="input-address" />
            <Button onClick={handleCreate} className="bg-primary hover:bg-primary/90" data-testid="btn-create-user"><UserPlus className="h-4 w-4 mr-2" />Créer le compte</Button>
          </CardContent>
        </Card>

        {/* Liste utilisateurs */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-base">Comptes utilisateurs ({users.length})</CardTitle>
              <div className="flex gap-2">
                <Input placeholder="Rechercher (email, nom)" value={search} onChange={(e) => setSearch(e.target.value)} className="w-56" data-testid="input-search-users" />
                <Button variant="outline" size="sm" onClick={loadAll} data-testid="btn-refresh-users"><RefreshCw className="h-4 w-4" /></Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-muted text-left">
                  <th className="p-2">Nom</th><th className="p-2">Email</th><th className="p-2">Rôle</th>
                  <th className="p-2">Dernière connexion</th><th className="p-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t" data-testid="user-row">
                    <td className="p-2">{[u.first_name, u.last_name].filter(Boolean).join(' ') || '—'}</td>
                    <td className="p-2">{u.email}</td>
                    <td className="p-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${u.user_type === 'fondateur' ? 'bg-red-500/15 text-red-600' : u.user_type === 'invite' ? 'bg-blue-500/15 text-blue-600' : 'bg-muted text-muted-foreground'}`}>{roleLabel(u.user_type)}</span>
                    </td>
                    <td className="p-2">{u.last_login ? new Date(u.last_login).toLocaleString('fr-FR') : 'Jamais'}</td>
                    <td className="p-2">
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" className="text-amber-600 hover:bg-amber-500/10" onClick={() => handleResetPassword(u)} title="Réinitialiser le mot de passe" data-testid={`btn-reset-pw-${u.email}`}><KeyRound className="h-3.5 w-3.5" /></Button>
                        {u.user_type !== 'fondateur' && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => handleForceLogout(u)} title="Déconnecter partout" data-testid={`btn-force-logout-${u.email}`}><LogOut className="h-3.5 w-3.5" /></Button>
                            <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-500/10" onClick={() => handleDelete(u)} title="Supprimer" data-testid={`btn-delete-${u.email}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && <tr><td className="p-3 text-muted-foreground" colSpan="5">Aucun compte</td></tr>}
              </tbody>
            </table>
            {resetInfo && (
              <div className="mt-3 rounded-md bg-emerald-500/10 border border-emerald-500/30 p-3 text-sm flex flex-wrap items-center gap-2" data-testid="reset-pw-result">
                <span>Nouveau mot de passe pour <b>{resetInfo.email}</b> :</span>
                <code className="px-2 py-1 rounded bg-background border font-mono" data-testid="reset-pw-value">{resetInfo.password}</code>
                <Button size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(resetInfo.password); flash('Mot de passe copié'); }} data-testid="btn-copy-reset-pw"><Copy className="h-3.5 w-3.5 mr-1" />Copier</Button>
                <Button size="sm" variant="ghost" onClick={() => setResetInfo(null)}>Fermer</Button>
                <span className="w-full text-xs text-muted-foreground">Communiquez-le à la personne concernée. Ses sessions actives ont été déconnectées.</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminConsole;
